import { Alert, Box, Button, Card, CardContent, Checkbox, FormControlLabel, FormGroup, Stack, Step, StepLabel, Stepper, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { LoadingState, PageHeader } from "../components";
import help from "./HELP.md?raw";
import { useOnboarding, useSaveStep, type Onboarding } from "./useData";

const STEPS: Onboarding["wizard"]["step"][] = ["company", "site", "team", "modules"];
const LABELS = { company: "Company", site: "First plant", team: "Your team", modules: "Modules", done: "Done" };
const MODULES = [
  ["quoting", "Quoting & estimating"], ["engineering", "Engineering (items, BOMs, routings)"], ["planning", "Planning & MRP"],
  ["production", "Shop floor & work orders"], ["inventory", "Inventory & warehouse"], ["purchasing", "Purchasing"],
  ["quality", "Quality management"], ["maintenance", "Maintenance"], ["finance", "Finance"], ["service", "Field service"],
];

export function OnboardingWizard() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const onboarding = useOnboarding();
  const save = useSaveStep();
  const [company, setCompany] = useState({ name: "", timezone: "", base_currency: "" });
  const [site, setSite] = useState({ code: "MAIN", name: "Main plant" });
  const [emails, setEmails] = useState("");
  const [modules, setModules] = useState<string[]>([]);

  useEffect(() => {
    if (me?.active_organization) setCompany({ name: me.active_organization.name, timezone: me.active_organization.timezone, base_currency: me.active_organization.base_currency });
  }, [me]);
  useEffect(() => { if (onboarding.data?.wizard.modules) setModules(onboarding.data.wizard.modules); }, [onboarding.data]);

  if (onboarding.isLoading || !onboarding.data) return <LoadingState />;
  const step = onboarding.data.wizard.step;
  if (step === "done" || onboarding.data.wizard.completed) {
    return (
      <>
        <PageHeader help={help} title="You're all set" subtitle="Your workspace is ready. Here is what to do next." />
        <Card><CardContent>
          <Stack spacing={1}>
            {onboarding.data.checklist.map((c) => (
              <Typography key={c.key} sx={{ textDecoration: c.done ? "line-through" : "none" }}>{c.done ? "✓" : "○"} {c.label}</Typography>
            ))}
            <Box><Button variant="contained" onClick={() => navigate("/app")}>Go to dashboard</Button></Box>
          </Stack>
        </CardContent></Card>
      </>
    );
  }
  const idx = STEPS.indexOf(step);
  const submit = () => {
    const payload = step === "company" ? company : step === "site" ? site
      : step === "team" ? { emails: emails.split(/[\s,;]+/).filter(Boolean) } : { modules };
    save.mutate({ step, payload });
  };
  return (
    <>
      <PageHeader help={help} title="Set up your workspace" subtitle="A few quick steps. You can change everything later in Settings." />
      <Stepper activeStep={idx} sx={{ mb: 3 }}>{STEPS.map((s) => <Step key={s}><StepLabel>{LABELS[s]}</StepLabel></Step>)}</Stepper>
      <Card sx={{ maxWidth: 640 }}><CardContent>
        <Stack spacing={2}>
          {save.isError && <Alert severity="error">{save.error.message}</Alert>}
          {step === "company" && (
            <>
              <TextField label="Company name" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} required />
              <TextField label="Timezone" value={company.timezone} onChange={(e) => setCompany({ ...company, timezone: e.target.value })} />
              <TextField label="Base currency" value={company.base_currency} onChange={(e) => setCompany({ ...company, base_currency: e.target.value.toUpperCase() })} inputProps={{ maxLength: 3 }} />
            </>
          )}
          {step === "site" && (
            <>
              <Typography variant="body2" color="text.secondary">Your first plant or facility. Add more under Settings → Sites.</Typography>
              <TextField label="Site code" value={site.code} onChange={(e) => setSite({ ...site, code: e.target.value.toUpperCase() })} />
              <TextField label="Site name" value={site.name} onChange={(e) => setSite({ ...site, name: e.target.value })} />
            </>
          )}
          {step === "team" && (
            <TextField label="Invite teammates by email" multiline minRows={3} value={emails} onChange={(e) => setEmails(e.target.value)}
              helperText="Separate addresses with commas or new lines. They get an email to join; you assign roles later." />
          )}
          {step === "modules" && (
            <FormGroup>
              {MODULES.map(([key, label]) => (
                <FormControlLabel key={key} label={label} control={<Checkbox checked={modules.includes(key)}
                  onChange={(e) => setModules((m) => e.target.checked ? [...m, key] : m.filter((x) => x !== key))} />} />
              ))}
            </FormGroup>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={() => save.mutate({ step, payload: {} })} disabled={save.isPending}>Skip</Button>
            <Button variant="contained" onClick={submit} disabled={save.isPending}>{idx === STEPS.length - 1 ? "Finish" : "Continue"}</Button>
          </Stack>
        </Stack>
      </CardContent></Card>
    </>
  );
}
